import React, { useEffect, useState } from "react";
import { Card, CardContent, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useDataProvider } from "react-admin";
import { useTheme } from "@mui/material/styles";

interface SloMetChartProps {
  releaseName: string;
  releaseVersion: string;
  sloField: "adjudicationSloMet" | "fixSloMet";
}

const SloMetChart: React.FC<SloMetChartProps> = ({
  releaseName,
  releaseVersion,
  sloField,
}) => {
  const theme = useTheme();
  const [data, setData] = useState<{ name: string; value: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const dataProvider = useDataProvider();

  useEffect(() => {
    const defaultData = [
      {
        name: "Met",
        value: 0,
      },
      {
        name: "Not Met",
        value: 0,
      },
    ];
    let isMounted = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const response = await dataProvider.getChart(
          "cve/analytics/slo-met-chart",
          {
            releaseName,
            releaseVersion,
            sloField,
          }
        );
        if (isMounted) {
          setData(
            defaultData.map((d) => ({
              ...d,
              value:
                response.find(
                  (r: { name: string; value: number }) => r.name === d.name
                )?.value || 0,
            }))
          );
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError("Failed to fetch SLO data");
          setLoading(false);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [dataProvider, releaseName, releaseVersion, sloField]);
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const option = {
    title: {
      text:
        sloField === "adjudicationSloMet"
          ? "Adjudication SLO Met"
          : "Fix SLO Met",
      left: "center",
      textStyle: {
        color: theme.palette.mode === "dark" ? "#fff" : "#222",
        fontWeight: "bold",
        fontSize: "1.5em",
      },
    },
    series: [
      {
        type: "gauge",
        center: ["50%", "55%"],
        startAngle: 200,
        endAngle: -20,
        min: 0,
        max: total || 1,
        itemStyle: {
          color: "rgb(100, 59, 177)",
          opacity: 0.75,
        },
        pointer: {
          show: false,
        },
        progress: {
          show: true,
          width: 30,
        },
        axisLine: {
          lineStyle: {
            width: 20,

            color: [[1, theme.palette.mode === "dark" ? "#b5b8c5" : "#edf0fa"]],
          },
        },
        splitLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        anchor: {
          show: false,
        },
        data: data && data.length > 0 ? [data[0]] : [],
        title: {
          fontSize: "1.2em",
          color: theme.palette.mode === "dark" ? "#fff" : "#222",
          show: false,
        },
        detail: {
          valueAnimation: true,
          width: "60%",
          borderRadius: 8,
          offsetCenter: [0, "60%"],
          fontSize: "2em",
          fontWeight: "bolder",
          formatter: () =>
            `${
              data[0].value > 0 && total > 0
                ? ((data[0].value / total) * 100).toFixed(2) + "%"
                : "N/A"
            }`,
          color: "inherit",
        },
      },
    ],
    tooltip: {
      formatter: () =>
        data.map((d) => `${d.name}: ${d.value}`).join("<br>") || "No data",
    },
  };

  return (
    <Card>
      <CardContent>
        {loading ? (
          <Typography>Loading...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <ReactECharts
            option={option}
            style={{ height: "250px", width: "100%" }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default SloMetChart;
